import {useAppSelector, useModal, useDialog} from '@hooks';
import {ErrorHandlingResult} from '@lib/infrastructure';
import {selectTimeslot, useAppDispatch, withAsyncThunkErrorHandling} from '@lib/store';
import {actions} from '@lib/store/src/data/timeslots/actions';
import {LocalDateString, Workday, WorkdayDetails} from '@lib/store/src/data/timeslots/apiTypes';
import {BookingLocationCellItem, BookWorkspaceCard, ModalPage} from '@organisms';
import {Div, FlexCol, P, Subtitle} from '@quarks';
import {useTranslation} from 'react-i18next';
import {
  getBuildingIdsThatRequireWorkspaceBooking,
  getBuildingSections,
} from '@lib/store/src/data/timeslots/selectors/getBuildingSections';
import {useState} from 'react';
import {
  getConfirmedWorkspaceReservationsForWorkday,
  getIsOfficeDayBookableBasedOnQuota,
} from '@lib/store/src/data/timeslots/selectors';
import {isOfficeWorkday} from '@lib/store/src/data/timeslots/utils';
import {useMemoDate} from '@lib/store/src/ui/hooks/useMemoDate';
import {dateKeyForDateTimeString} from '@lib/store/src/data/timeslots/dateTimeHelpers';
import {ensureNoParkingSpotOnDate} from '@lib/store/src/data/parkingReservation/ensureNoParkingSpotOnDate';

export const BookingLocationStatusCard = ({date, workday}: {date: LocalDateString; workday: Workday | null}) => {
  const [isSaving, setIsSaving] = useState(false);

  const {t} = useTranslation();
  const {destructiveDialog} = useDialog();
  const {closeModal, addPages} = useModal();
  const dispatch = useAppDispatch();
  const dateObj = useMemoDate(date);

  const buildingIdsWithRequiredWorkspaceBooking = useAppSelector(getBuildingIdsThatRequireWorkspaceBooking);
  const buildingSections = useAppSelector((state) => getBuildingSections(state, date, workday));
  const isOfficeDayBookableBasedOnQuota = useAppSelector(getIsOfficeDayBookableBasedOnQuota(dateObj));

  const cancelWorkday = () => {
    if (workday) dispatch(withAsyncThunkErrorHandling(() => actions.cancelWorkdayIncludingReservations({workday})));
  };

  const currentWorkdayHasConfirmedWorkspaceReservations =
    useAppSelector(getConfirmedWorkspaceReservationsForWorkday(workday?.id ?? null)).length > 0;

  const showCancellationDialog = () =>
    destructiveDialog(
      t('workplace:DeleteYourBookings'),
      t('workplace:DeleteYourBookingsMessage', {
        buildingName: workday?.status === 'OfficeDay' ? workday.buildingName : undefined,
      }),
      'reportProblem',
    );

  const createWorkday = (details: WorkdayDetails) => {
    return dispatch(
      withAsyncThunkErrorHandling(() =>
        actions.createWorkday({
          ...details,
          // TODO: set start/end times
          localStart: `${date}T00:00`,
          localEnd: `${date}T23:59`,
        }),
      ),
    );
  };
  const editWorkdayIfneeded = async (
    workday: Workday,
    details: WorkdayDetails,
  ): Promise<ErrorHandlingResult<Workday | null>> => {
    const hasChange =
      workday.status !== details.status ||
      (workday.status === 'OfficeDay' && details.status === 'OfficeDay' && workday.buildingId !== details.buildingId);

    if (hasChange) {
      if (currentWorkdayHasConfirmedWorkspaceReservations && isOfficeWorkday(workday)) {
        await dispatch(actions.cancelWorkspaceReservationsOnWorkday({workday})).unwrap();
      }

      await dispatch(ensureNoParkingSpotOnDate(dateKeyForDateTimeString(workday.localStart))).unwrap();

      return dispatch(
        withAsyncThunkErrorHandling(() =>
          actions.updateWorkday({
            id: workday.id,
            localStart: workday.localStart,
            localEnd: workday.localEnd,
            ...details,
          }),
        ),
      );
    }

    return Promise.resolve({result: null, success: true});
  };

  const handleClick = async (details: WorkdayDetails | null) => {
    const isActualChange = () => {
      if (workday) {
        // Cancel something
        if (!details) return true;

        // Update type of plans
        if (details.status !== workday.status) return true;

        // Change building
        if (details.status === 'OfficeDay' && workday.status === 'OfficeDay')
          return details.buildingId !== workday.buildingId;

        // Note: at some point you'd check times here
        return false;
      } else {
        // Create something
        return details !== null;
      }
    };

    if (!isActualChange()) return;

    if (currentWorkdayHasConfirmedWorkspaceReservations) {
      const confirmation = await showCancellationDialog();
      if (!confirmation) return;
    }

    if (!details) {
      cancelWorkday();
      closeModal();
      return;
    }

    setIsSaving(true);

    const wdEdit = workday ? editWorkdayIfneeded(workday, details) : createWorkday(details);

    const workdayRequiresWorkspace =
      details.status === 'OfficeDay' && buildingIdsWithRequiredWorkspaceBooking.has(details.buildingId);
    if (workdayRequiresWorkspace) {
      const result = await wdEdit;
      if (result.success) {
        const updatedWorkday = result.result;

        if (updatedWorkday?.status === 'OfficeDay') {
          setIsSaving(false);

          dispatch(
            selectTimeslot({
              startTime: updatedWorkday.localStart,
              endTime: updatedWorkday.localEnd,
            }),
          );

          addPages([<BookWorkspaceCard workday={updatedWorkday} />]);
        } else {
          closeModal();
        }
      }
    } else {
      // Close and rely on loading state of calendar
      closeModal();
    }
  };

  return (
    <ModalPage
      onClose={closeModal}
      title={t('workplace:YourLocation')}>
      <FlexCol gap={32}>
        <Div>
          <BookingLocationCellItem
            icon="calendarX"
            isActive={workday?.status === 'NotWorking'}
            isDisabled={isSaving}
            canNavigate={false}
            onClick={() =>
              handleClick({
                status: 'NotWorking',
              })
            }
            title={t('workplace:NotWorking')}
          />

          <BookingLocationCellItem
            icon="remote"
            isActive={workday?.status === 'WorkingRemotely'}
            isDisabled={isSaving}
            canNavigate={false}
            onClick={() => handleClick({status: 'WorkingRemotely'})}
            title={t('workplace:WorkingRemotely')}
          />

          {buildingSections.open.map((b) => {
            const isActive = workday?.status === 'OfficeDay' && workday.buildingId === b.id;

            return (
              <BookingLocationCellItem
                key={`open_${b.id}`}
                icon="office"
                isActive={isActive}
                isDisabled={isSaving || !isOfficeDayBookableBasedOnQuota}
                canNavigate={!isActive}
                title={b.name}
                // Note: we no longer show availability at this stage since timeslots
                subTitle={
                  buildingIdsWithRequiredWorkspaceBooking.has(b.id) ? t('workplace:WorkspaceBookingRequired') : null
                }
                onClick={() =>
                  handleClick({
                    buildingId: b.id,
                    buildingName: b.name,
                    status: 'OfficeDay',
                    buildingIsValid: true,
                  })
                }
              />
            );
          })}

          {buildingSections.closed.length > 0 && (
            <Div className="mt-5">
              <P
                fontWeight="bold"
                color="currentColor">
                {t('workplace:WorkspaceGroupUnavailable')}
              </P>

              {buildingSections.closed.map((b) => {
                const isActive = workday?.status === 'OfficeDay' && workday.buildingId === b.id;

                return (
                  <BookingLocationCellItem
                    key={`closed_${b.id}`}
                    icon="office"
                    isActive={isActive}
                    isDisabled
                    canNavigate={!isActive}
                    title={b.name}
                    subTitle={t('workplace:ClosedOffice')}
                    onClick={() =>
                      handleClick({
                        buildingId: b.id,
                        buildingName: b.name,
                        status: 'OfficeDay',
                        buildingIsValid: true,
                      })
                    }
                  />
                );
              })}
            </Div>
          )}
        </Div>

        {workday && (
          <BookingLocationCellItem
            icon="trash"
            isActive={false}
            isDisabled={isSaving}
            canNavigate={false}
            onClick={() => handleClick(null)}
            title={t('workplace:ClearWorkday')}
          />
        )}
        <Subtitle fontSize="sm">{t('workplace:NoOfficeInTheList')}</Subtitle>
      </FlexCol>
    </ModalPage>
  );
};
